// Deterministic parser for revision plan outputs. The plans come in several
// legacy shapes plus the new canonical template:
//   1. New canonical: ## Critical/Secondary Revision Priorities → ### N. Title
//      with an **Action Items:** list, ## Revision Sequence with ### Phase N.
//   2. Legacy memoir/non-fiction: ## Priority 1: Critical Revisions with
//      #### Issue: Title blocks and **How to address it**: steps.
//   3. Legacy novel: ## PHASE N: TITLE sections containing ### Priority N /
//      ### Minor Improvement N with **Implementation Steps**:.
//   4. Legacy short-story: ## Section N: Critical/Secondary Revision
//      Priorities with ### Priority N: Title and **Action steps:**.
// The parser classifies generic ## sections by title keywords and detects
// priority blocks inside any of them. ok:false → render as plain markdown.

export type PriorityLevel = 'critical' | 'secondary' | 'minor' | 'unknown';
export type SectionKind = 'priorities' | 'sequence' | 'prose';

export interface ActionItem {
  id: string;
  text: string;
  markdown: string;
}

export interface RevisionPriority {
  id: string;
  index: number;
  title: string;
  level: PriorityLevel;
  bodyMarkdown: string;
  actions: ActionItem[];
}

export interface RevisionPhase {
  id: string;
  name: string;
  bodyMarkdown: string;
  priorityRefs: number[];
}

export interface RevisionSection {
  id: string;
  title: string;
  kind: SectionKind;
  level: PriorityLevel;
  bodyMarkdown: string;
  priorities: RevisionPriority[];
  phases: RevisionPhase[];
}

export interface ParsedRevisionPlan {
  title: string;
  intro: string;
  sections: RevisionSection[];
  totalActions: number;
  ok: boolean;
}

// djb2 hash of normalized text; stable identity for checkbox persistence.
export function hashActionText(text: string): string {
  const normalized = text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?]+$/g, '')
    .trim();
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) + hash + normalized.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

const PRIORITY_HEADING = /^#{3,4}\s+(?:priority|minor improvement|issue)[\s:]*(\d+)?[:.\s]*(.*?)\s*$/i;
// New canonical template: "### 1. Title" (numbering continues across sections)
const NUMBERED_HEADING = /^#{3,4}\s+(\d+)[.)]\s+(.*?)\s*$/;
const ISSUE_HEADING = /^#{3,4}\s+issue:\s*(.*?)\s*$/i;
const PHASE_HEADING = /^#{3,4}\s+phase\s+(\d+)[:.\s]*(.*?)\s*$/i;
// Bold field names that introduce the action list in each legacy shape.
// Colons appear both inside (**Action steps:**) and outside (**Implementation Steps**:)
const ACTION_FIELD = /^\*\*(?:action items?|action steps?|implementation steps?|how to address it):?\*\*:?\s*$/i;

function sectionLevel(title: string): PriorityLevel {
  if (/critical/i.test(title)) return 'critical';
  if (/secondary|important|high/i.test(title)) return 'secondary';
  if (/minor|polish|refinement/i.test(title)) return 'minor';
  return 'unknown';
}

function sectionKind(title: string, hasPriorities: boolean, hasPhases: boolean): SectionKind {
  if (hasPriorities) return 'priorities';
  if (hasPhases || /sequence|roadmap|workflow/i.test(title)) return 'sequence';
  return 'prose';
}

function stripListMarker(line: string): string {
  return line.replace(/^\s*(?:[*+-]|\d+\.)\s+/, '').trim();
}

function isListItem(line: string): boolean {
  return /^\s*(?:[*+-]|\d+\.)\s+/.test(line);
}

// Split a priority block's body into prose and its action checklist. Actions
// are the top-level list items following an action field label; if no label
// exists, no checkboxes are produced (rationale-only priority).
function extractActions(body: string): { bodyMarkdown: string; actions: ActionItem[] } {
  const lines = body.split('\n');
  const proseLines: string[] = [];
  const actions: ActionItem[] = [];
  let inActionList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (ACTION_FIELD.test(line.trim())) {
      inActionList = true;
      continue;
    }
    if (inActionList) {
      if (isListItem(line)) {
        // Nested continuation items (indented) merge into the previous action.
        const indented = /^\s{2,}/.test(line);
        if (indented && actions.length > 0) {
          actions[actions.length - 1]!.markdown += '\n' + line;
        } else {
          const text = stripListMarker(line);
          actions.push({ id: hashActionText(text), text, markdown: line });
        }
        continue;
      }
      if (line.trim() === '') continue;
      // Non-list content ends the action list.
      inActionList = false;
    }
    proseLines.push(line);
  }

  return { bodyMarkdown: proseLines.join('\n').trim(), actions };
}

export function parseRevisionPlan(markdown: string): ParsedRevisionPlan {
  const result: ParsedRevisionPlan = { title: '', intro: '', sections: [], totalActions: 0, ok: false };
  if (!markdown?.trim()) return result;

  const lines = markdown.split('\n');

  // Document title
  const titleIdx = lines.findIndex(l => /^#\s+\S/.test(l));
  if (titleIdx !== -1) {
    result.title = lines[titleIdx]!.replace(/^#\s+/, '').trim();
  }

  // Split into ## sections
  interface RawSection {
    title: string;
    startLine: number;
    lines: string[];
  }
  const rawSections: RawSection[] = [];
  let current: RawSection | null = null;
  const introLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const h2 = line.match(/^##\s+(.+?)\s*$/);
    if (h2 && !/^#{3,}/.test(line)) {
      current = { title: h2[1]!, startLine: i, lines: [] };
      rawSections.push(current);
    } else if (current) {
      current.lines.push(line);
    } else if (i !== titleIdx) {
      introLines.push(line);
    }
  }
  result.intro = introLines.join('\n').trim();

  let priorityCounter = 0;

  for (let s = 0; s < rawSections.length; s++) {
    const raw = rawSections[s]!;
    const level = sectionLevel(raw.title);

    // Split section body into sub-blocks on ###/#### headings
    interface SubBlock {
      heading: string;
      lines: string[];
    }
    const preamble: string[] = [];
    const subBlocks: SubBlock[] = [];
    let sub: SubBlock | null = null;

    for (const line of raw.lines) {
      if (/^#{3,4}\s+\S/.test(line)) {
        sub = { heading: line, lines: [] };
        subBlocks.push(sub);
      } else if (sub) {
        sub.lines.push(line);
      } else {
        preamble.push(line);
      }
    }

    const priorities: RevisionPriority[] = [];
    const phases: RevisionPhase[] = [];

    for (const block of subBlocks) {
      const phaseMatch = block.heading.match(PHASE_HEADING);
      const issueMatch = block.heading.match(ISSUE_HEADING);
      const priorityMatch = block.heading.match(PRIORITY_HEADING) ?? block.heading.match(NUMBERED_HEADING);
      const body = block.lines.join('\n').trim();

      if (phaseMatch) {
        const name = phaseMatch[2]?.trim()
          ? `Phase ${phaseMatch[1]}: ${phaseMatch[2]!.trim()}`
          : `Phase ${phaseMatch[1]}`;
        const priorityRefs = [...body.matchAll(/priority\s+(\d+)/gi)].map(m => parseInt(m[1]!, 10));
        phases.push({
          id: `revision-phase-${phaseMatch[1]}`,
          name,
          bodyMarkdown: body,
          priorityRefs: [...new Set(priorityRefs)],
        });
      } else if (issueMatch || priorityMatch) {
        priorityCounter++;
        const heading = block.heading.replace(/^#+\s*/, '').trim();
        const explicitIndex = priorityMatch?.[1] ? parseInt(priorityMatch[1]!, 10) : priorityCounter;
        const title = issueMatch?.[1]?.trim() || priorityMatch?.[2]?.trim() || heading;
        const blockLevel = level !== 'unknown' ? level : /minor improvement/i.test(block.heading) ? 'minor' : 'unknown';
        const { bodyMarkdown, actions } = extractActions(body);
        priorities.push({
          id: `revision-priority-${priorityCounter}`,
          index: explicitIndex,
          title,
          level: blockLevel,
          bodyMarkdown,
          actions,
        });
      } else {
        // Unrecognized sub-heading: keep it in the section preamble so no
        // content is dropped.
        preamble.push(block.heading, ...block.lines);
      }
    }

    result.sections.push({
      id: `revision-section-${s}`,
      title: raw.title,
      kind: sectionKind(raw.title, priorities.length > 0, phases.length > 0),
      level,
      bodyMarkdown: preamble.join('\n').trim(),
      priorities,
      phases,
    });
  }

  result.totalActions = result.sections.reduce(
    (sum, section) => sum + section.priorities.reduce((n, p) => n + p.actions.length, 0),
    0
  );
  result.ok = result.sections.some(sec => sec.priorities.length > 0);
  return result;
}
